import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { AlertCircle } from 'lucide-react';
import { BookingFormValues, Product } from '../types';
import { useToast } from '../context/ToastContext';
import { sendBookingConfirmation } from '../utils/emailService';
import { createOrder } from '../utils/orders';

const bookingSchema = Yup.object().shape({
  fullName: Yup.string()
    .required('Full name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address'),
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Please enter a valid 10-digit phone number')
    .required('Phone number is required'),
  street: Yup.string()
    .required('Street address is required'),
  city: Yup.string()
    .required('City is required'),
  state: Yup.string()
    .required('State is required')
    .matches(/^[A-Z]{2}$/, 'Please enter a valid 2-letter state code'),
  zipCode: Yup.string()
    .required('ZIP code is required')
    .matches(/^\d{5}$/, 'Please enter a valid ZIP code'),
  installationLocation: Yup.string()
    .required('Installation location is required'),
  hasTrade: Yup.boolean(),
  requiresHaulAway: Yup.boolean()
    .when('hasTrade', {
      is: true,
      then: (schema) => schema.required('Please specify if haul-away is required')
    }),
  notes: Yup.string(),
});

interface BookingFormProps {
  product: Product;
  onBookingComplete: (details: {
    orderNumber: string;
    customerName: string;
  }) => void;
}

export function BookingForm({ product, onBookingComplete }: BookingFormProps) {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: BookingFormValues) => {
    try {
      setIsSubmitting(true);
      const orderNumber = `ORD-${Date.now().toString(36).toUpperCase()}`;

      const bookingData = {
        id: Date.now().toString(),
        order_number: orderNumber,
        customer_name: values.fullName,
        customer_email: values.email || null,
        customer_phone: values.phone,
        product: {
          id: product.id,
          name: product.name,
          price: product.price,
          brand: product.brand,
          type: product.type,
          image: product.images?.[0]?.url
        },
        delivery_address: {
          street: values.street,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode
        },
        installation_location: values.installationLocation,
        has_trade: values.hasTrade,
        requires_haul_away: values.hasTrade ? values.requiresHaulAway : null,
        status: 'Pending' as const,
        payment_method: 'onInstallation' as const,
        status_history: [{
          status: 'Pending',
          timestamp: new Date().toISOString(),
          note: 'Order created'
        }],
        notes: values.notes,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Create order in Supabase
      const { success, error } = await createOrder(bookingData);
      
      if (!success) {
        throw new Error(error || 'Failed to create order');
      }

      // Send confirmation email if email is provided
      if (values.email) {
        const emailSent = await sendBookingConfirmation(bookingData);
        if (!emailSent) {
          console.error('Failed to send confirmation email');
          // Continue with booking process even if email fails
        }
      }

      showToast('Installation booking confirmed!', 'success');
      
      onBookingComplete({
        orderNumber: bookingData.order_number,
        customerName: values.fullName,
      });
    } catch (error) {
      console.error('Booking error:', error);
      showToast(error.message || 'Failed to create booking. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        fullName: '',
        email: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        installationLocation: '',
        hasTrade: false,
        requiresHaulAway: false,
        notes: '',
      }}
      validationSchema={bookingSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, values }) => (
        <Form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Full Name *
              </label>
              <Field
                id="fullName"
                name="fullName"
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.fullName && touched.fullName && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.fullName}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email (Optional)
              </label>
              <Field
                id="email"
                name="email"
                type="email"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.email && touched.email && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.email}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number *
              </label>
              <Field
                id="phone"
                name="phone"
                type="tel"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.phone && touched.phone && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.phone}
                </div>
              )}
            </div>
          </div>

          {/* Address Fields */}
          <div className="space-y-4">
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                Street Address *
              </label>
              <Field
                id="street"
                name="street"
                type="text"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.street && touched.street && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.street}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City *
                </label>
                <Field
                  id="city"
                  name="city"
                  type="text"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.city && touched.city && (
                  <div className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.city}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State *
                </label>
                <Field
                  id="state"
                  name="state"
                  type="text"
                  maxLength={2}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.state && touched.state && (
                  <div className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.state}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                  ZIP Code *
                </label>
                <Field
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  maxLength={5}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                {errors.zipCode && touched.zipCode && (
                  <div className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.zipCode}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Installation Location */}
          <div>
            <label htmlFor="installationLocation" className="block text-sm font-medium text-gray-700">
              Where will it be installed? *
            </label>
            <Field
              as="select"
              id="installationLocation"
              name="installationLocation"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select location</option>
              <option value="1st Floor">1st Floor</option>
              <option value="2nd Floor">2nd Floor</option>
              <option value="Garage">Garage</option>
              <option value="Back Yard">Back Yard</option>
            </Field>
            {errors.installationLocation && touched.installationLocation && (
              <div className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.installationLocation}
              </div>
            )}
          </div>

          {/* Trade-In Options */}
          <div className="space-y-4">
            <div className="flex items-center">
              <Field
                type="checkbox"
                id="hasTrade"
                name="hasTrade"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="hasTrade" className="ml-2 block text-sm text-gray-700">
                I have a trade-in
              </label>
            </div>

            {values.hasTrade && (
              <div className="ml-6">
                <div className="flex items-center space-x-4">
                  <label className="text-sm text-gray-700">Will the trade require a haul-away?</label>
                  <div className="space-x-4">
                    <label className="inline-flex items-center">
                      <Field
                        type="radio"
                        name="requiresHaulAway"
                        value="true"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <Field
                        type="radio"
                        name="requiresHaulAway"
                        value="false"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>
                {errors.requiresHaulAway && touched.requiresHaulAway && (
                  <div className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.requiresHaulAway}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Additional Notes
            </label>
            <Field
              as="textarea"
              id="notes"
              name="notes"
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Any special instructions or requirements for the installation..."
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Important Information</h3>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
              <li>Payment is due upon successful installation</li>
              <li>Our technician will contact you 30 minutes before arrival</li>
              <li>Please ensure someone over 18 is present during installation</li>
              <li>Cancellations must be made at least 24 hours in advance</li>
              <li>We only service locations within 30 miles of Santa Ana, CA</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Installation'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}